import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo, type ReactNode } from 'react'
import { notifications as notificationsApi, messages as messagesApi } from '../api'

interface UnreadCounts {
  notifications: number
  messages: number
  /** Force a refresh of both counts right now (e.g. after marking a message as read). */
  refresh: () => void
}

const UnreadCountsContext = createContext<UnreadCounts | null>(null)

const POLL_INTERVAL = 30_000 // 30 seconds

/**
 * Single polling provider for notification + message unread counts.
 * Replaces the independent polling in Sidebar (notifications) and TopBar (messages).
 * Dashboard can also consume this instead of making its own one-shot calls.
 */
export function UnreadCountsProvider({ children }: { children: ReactNode }) {
  const [notifCount, setNotifCount] = useState(0)
  const [msgCount, setMsgCount] = useState(0)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const fetchAll = useCallback(() => {
    notificationsApi.unreadCount().then((r) => setNotifCount(r.count)).catch(() => {})
    messagesApi.unreadCount().then((r) => setMsgCount(r.count)).catch(() => {})
  }, [])

  useEffect(() => {
    fetchAll()
    pollRef.current = setInterval(fetchAll, POLL_INTERVAL)
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [fetchAll])

  const value = useMemo<UnreadCounts>(
    () => ({ notifications: notifCount, messages: msgCount, refresh: fetchAll }),
    [notifCount, msgCount, fetchAll]
  )

  return (
    <UnreadCountsContext.Provider value={value}>
      {children}
    </UnreadCountsContext.Provider>
  )
}

export function useUnreadCounts(): UnreadCounts {
  const ctx = useContext(UnreadCountsContext)
  if (!ctx) throw new Error('useUnreadCounts must be used within an UnreadCountsProvider')
  return ctx
}
